import React, { useState } from 'react';
import type { CampaignStats } from '../lib/campaigns.js';

export interface CampaignAnalyticsDashboardProps {
  stats?: CampaignStats | null;
}

export function CampaignAnalyticsDashboard({ stats }: CampaignAnalyticsDashboardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'funnel' | 'heatmap' | 'deliverability'>('overview');

  const s = stats ?? {
    total: 0,
    queued: 0,
    sending: 0,
    sent: 0,
    failed: 0,
    skipped: 0,
    uniqueOpens: 0,
    uniqueClicks: 0,
    machineOpens: 0,
    unsubscribes: 0,
    bounces: 0,
    openRate: null,
    clickRate: null,
    skipReasons: {},
    linkStats: [],
  };

  const total = s.total ?? 0;
  const sent = s.sent ?? 0;
  const queued = s.queued ?? 0;
  const uniqueOpens = s.uniqueOpens ?? 0;
  const uniqueClicks = s.uniqueClicks ?? 0;
  const bounces = s.bounces ?? 0;
  const failed = s.failed ?? 0;
  const unsubscribes = s.unsubscribes ?? 0;
  const machineOpens = s.machineOpens ?? 0;
  const skipReasons = s.skipReasons ?? {};
  const linkStats = s.linkStats ?? [];

  const percent = (value: number | null | undefined) =>
    value === null || value === undefined ? '0,0 %' : `${(value * 100).toFixed(1).replace('.', ',')} %`;

  const deliveryRate = total > 0 ? (sent / total) * 100 : 0;
  const openRatePercent = s.openRate !== null && s.openRate !== undefined ? s.openRate * 100 : 0;
  const clickRatePercent = s.clickRate !== null && s.clickRate !== undefined ? s.clickRate * 100 : 0;
  
  // CTOR (Click to Open Rate)
  const ctor = uniqueOpens > 0 ? (uniqueClicks / uniqueOpens) * 100 : 0;

  return (
    <div className="analytics-dashboard stack" style={{ gap: '1.5rem' }}>
      {/* CSS STYLES FOR 3D DASHBOARD KIT */}
      <style>{`
        .analytics-dashboard {
          font-family: var(--font-sans, system-ui, sans-serif);
        }
        .dashboard-tab-bar {
          display: flex;
          gap: 0.5rem;
          background: rgba(15, 23, 42, 0.6);
          padding: 0.35rem;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(12px);
        }
        .dashboard-tab {
          flex: 1;
          padding: 0.6rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #94a3b8;
          background: transparent;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        .dashboard-tab:hover {
          color: #f8fafc;
          background: rgba(255, 255, 255, 0.05);
        }
        .dashboard-tab.active {
          color: #ffffff;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          box-shadow: 0 4px 14px rgba(37, 99, 235, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2);
        }
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.25rem;
        }
        .kpi-card {
          position: relative;
          background: linear-gradient(145deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.85) 100%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 1.25rem;
          backdrop-filter: blur(16px);
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.15);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
          overflow: hidden;
        }
        .kpi-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 32px -6px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.25);
          border-color: rgba(99, 102, 241, 0.4);
        }
        .kpi-card__badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.6rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.02em;
        }
        .kpi-card__icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
          margin-bottom: 0.75rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2);
        }
        .funnel-stage {
          position: relative;
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 1rem 1.25rem;
          overflow: hidden;
          transition: all 0.2s ease;
        }
        .funnel-stage:hover {
          border-color: rgba(99, 102, 241, 0.4);
          transform: translateX(4px);
        }
        .funnel-bar-fill {
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          opacity: 0.2;
          transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 12px 0 0 12px;
        }
        .donut-container {
          position: relative;
          width: 160px;
          height: 160px;
        }
        .heat-item {
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          padding: 0.85rem 1rem;
          transition: all 0.2s ease;
        }
        .heat-item:hover {
          border-color: rgba(59, 130, 246, 0.4);
          background: rgba(30, 41, 59, 0.6);
        }
      `}</style>

      {/* DASHBOARD TAB NAVIGATION */}
      <div className="dashboard-tab-bar">
        <button
          type="button"
          className={`dashboard-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Dashboard KPI
        </button>
        <button
          type="button"
          className={`dashboard-tab ${activeTab === 'funnel' ? 'active' : ''}`}
          onClick={() => setActiveTab('funnel')}
        >
          🔻 Performance Funnel
        </button>
        <button
          type="button"
          className={`dashboard-tab ${activeTab === 'heatmap' ? 'active' : ''}`}
          onClick={() => setActiveTab('heatmap')}
        >
          🎯 Link Heatmap
        </button>
        <button
          type="button"
          className={`dashboard-tab ${activeTab === 'deliverability' ? 'active' : ''}`}
          onClick={() => setActiveTab('deliverability')}
        >
          🛡️ Zustellbarkeit &amp; Filter
        </button>
      </div>

      {/* TAB 1: OVERVIEW KPI GRID */}
      {activeTab === 'overview' && (
        <div className="stack" style={{ gap: '1.5rem' }}>
          <div className="kpi-grid">
            {/* KPI 1: ZUSTELLUNG */}
            <div className="kpi-card">
              <div
                className="kpi-card__icon"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#ffffff',
                }}
              >
                🚀
              </div>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
                Zustellung
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f8fafc', margin: '0.25rem 0' }}>
                {sent.toLocaleString('de-DE')}
                <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>
                  {' '}/ {total.toLocaleString('de-DE')}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                <span className="kpi-card__badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#34d399' }}>
                  {deliveryRate.toFixed(1)}% Erfolgsquote
                </span>
                {queued > 0 && (
                  <span className="text-xs text-muted">⌛ {queued} warten</span>
                )}
              </div>
              <div
                style={{
                  width: '100%',
                  height: '6px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '3px',
                  marginTop: '0.85rem',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, deliveryRate)}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #10b981, #34d399)',
                    borderRadius: '3px',
                  }}
                />
              </div>
            </div>

            {/* KPI 2: ÖFFNUNGEN */}
            <div className="kpi-card">
              <div
                className="kpi-card__icon"
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: '#ffffff',
                }}
              >
                📬
              </div>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
                Öffnungsrate
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f8fafc', margin: '0.25rem 0' }}>
                {percent(s.openRate)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                <span className="kpi-card__badge" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8' }}>
                  {uniqueOpens.toLocaleString('de-DE')} Eindeutige Öffnungen
                </span>
              </div>
              <div
                style={{
                  width: '100%',
                  height: '6px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '3px',
                  marginTop: '0.85rem',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, openRatePercent)}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #6366f1, #818cf8)',
                    borderRadius: '3px',
                  }}
                />
              </div>
            </div>

            {/* KPI 3: KLICKS */}
            <div className="kpi-card">
              <div
                className="kpi-card__icon"
                style={{
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  color: '#ffffff',
                }}
              >
                👆
              </div>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
                Klickrate (CTR)
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f8fafc', margin: '0.25rem 0' }}>
                {percent(s.clickRate)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                <span className="kpi-card__badge" style={{ background: 'rgba(6, 182, 212, 0.15)', color: '#22d3ee' }}>
                  {uniqueClicks.toLocaleString('de-DE')} Klicks ({ctor.toFixed(1)}% CTOR)
                </span>
              </div>
              <div
                style={{
                  width: '100%',
                  height: '6px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '3px',
                  marginTop: '0.85rem',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, clickRatePercent)}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #06b6d4, #22d3ee)',
                    borderRadius: '3px',
                  }}
                />
              </div>
            </div>

            {/* KPI 4: SPAM & BOUNCES */}
            <div className="kpi-card">
              <div
                className="kpi-card__icon"
                style={{
                  background: bounces > 0 || unsubscribes > 0
                    ? 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#ffffff',
                }}
              >
                {bounces > 0 ? '⚠️' : '✅'}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
                Reputation &amp; Bounces
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f8fafc', margin: '0.25rem 0' }}>
                {bounces} <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>Bounces</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                <span className="kpi-card__badge" style={{ background: 'rgba(244, 63, 94, 0.15)', color: '#fb7185' }}>
                  {unsubscribes} Abmeldungen
                </span>
                <span className="kpi-card__badge" style={{ background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8' }}>
                  {machineOpens} Bots gefiltert
                </span>
              </div>
            </div>
          </div>

          {/* STATUS DONUT & DISTRIBUTION SUMMARY */}
          <div className="card">
            <div className="card__body" style={{ padding: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.25rem', color: '#f8fafc' }}>
                📊 Status-Verteilung der Empfänger
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', alignItems: 'center' }}>
                {/* SVG DONUT CHART */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem' }}>
                  <div className="donut-container">
                    <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                      {/* Background Track */}
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="rgba(255, 255, 255, 0.08)"
                        strokeWidth="3.8"
                      />
                      {/* Sent Segment */}
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="3.8"
                        strokeDasharray={`${Math.max(0, deliveryRate)}, 100`}
                        style={{ transition: 'stroke-dasharray 1s ease' }}
                      />
                      {/* Open Segment */}
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#6366f1"
                        strokeWidth="3.8"
                        strokeDasharray={`${Math.max(0, openRatePercent)}, 100`}
                        style={{ transition: 'stroke-dasharray 1s ease' }}
                      />
                    </svg>
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f8fafc' }}>
                        {sent}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase' }}>
                        Gesendet
                      </span>
                    </div>
                  </div>

                  {/* LEGEND */}
                  <div className="stack" style={{ gap: '0.6rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10b981' }} />
                      <span>Versendet: <strong>{sent}</strong></span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#6366f1' }} />
                      <span>Geöffnet: <strong>{uniqueOpens}</strong></span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#06b6d4' }} />
                      <span>Klicks: <strong>{uniqueClicks}</strong></span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f43f5e' }} />
                      <span>Bounces/Fehler: <strong>{bounces + failed}</strong></span>
                    </div>
                  </div>
                </div>

                {/* SUMMARY DETAILS */}
                <div style={{ background: 'rgba(15, 23, 42, 0.4)', padding: '1rem 1.25rem', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <div style={{ fontWeight: 700, color: '#f8fafc', marginBottom: '0.5rem' }}>
                    💡 Kampagnen-Qualitätssignale
                  </div>
                  <ul className="stack text-sm text-muted" style={{ gap: '0.4rem', paddingLeft: '1.2rem', margin: 0 }}>
                    <li>
                      <strong>Öffnungsrate ({percent(s.openRate)}):</strong>{' '}
                      {openRatePercent >= 25 ? 'Exzellent! Über dem Branchenschnitt.' : openRatePercent >= 15 ? 'Guter Durchschnitt.' : 'Optimierung von Betreff & Absender empfohlen.'}
                    </li>
                    <li>
                      <strong>Klick-zu-Öffnungsrate ({ctor.toFixed(1)}%):</strong>{' '}
                      {ctor >= 10 ? 'Starke Call-to-Action Performance.' : 'Empfehlung: Links deutlicher hervorheben.'}
                    </li>
                    <li>
                      <strong>Maschinelles Tracking:</strong> {machineOpens} Bots wurden erkannt &amp; aus den echten Quoten gefiltered.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: PERFORMANCE FUNNEL */}
      {activeTab === 'funnel' && (
        <div className="card">
          <div className="card__body stack" style={{ gap: '1.25rem', padding: '1.5rem' }}>
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
                🔻 Kampagnen Konvertierungs-Funnel
              </h3>
              <p className="text-muted text-sm" style={{ marginTop: '0.25rem' }}>
                Visueller Trichter vom ersten Empfänger bis zur Interaktion.
              </p>
            </div>

            <div className="stack" style={{ gap: '0.75rem' }}>
              {/* STAGE 1: GESAMT */}
              <div className="funnel-stage">
                <div className="funnel-bar-fill" style={{ width: '100%', background: '#64748b' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                  <div>
                    <strong style={{ color: '#f8fafc' }}>1. Gesamt-Zielgruppe</strong>
                    <div className="text-xs text-muted">Ausgewählte Kontakte</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <strong style={{ fontSize: '1.2rem', color: '#f8fafc' }}>{total.toLocaleString('de-DE')}</strong>
                    <div className="text-xs text-muted">100%</div>
                  </div>
                </div>
              </div>

              {/* STAGE 2: VERSENDET */}
              <div className="funnel-stage">
                <div className="funnel-bar-fill" style={{ width: `${deliveryRate}%`, background: '#10b981' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                  <div>
                    <strong style={{ color: '#34d399' }}>2. Zugestellt &amp; Versendet</strong>
                    <div className="text-xs text-muted">Erfolgreich vom Mailserver übergeben</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <strong style={{ fontSize: '1.2rem', color: '#34d399' }}>{sent.toLocaleString('de-DE')}</strong>
                    <div className="text-xs text-muted">{deliveryRate.toFixed(1)}% der Zielgruppe</div>
                  </div>
                </div>
              </div>

              {/* STAGE 3: GEÖFFNET */}
              <div className="funnel-stage">
                <div className="funnel-bar-fill" style={{ width: `${openRatePercent}%`, background: '#6366f1' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                  <div>
                    <strong style={{ color: '#818cf8' }}>3. Eindeutig Geöffnet</strong>
                    <div className="text-xs text-muted">Reale menschliche Öffnungen</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <strong style={{ fontSize: '1.2rem', color: '#818cf8' }}>{uniqueOpens.toLocaleString('de-DE')}</strong>
                    <div className="text-xs text-muted">{percent(s.openRate)} der Versendeten</div>
                  </div>
                </div>
              </div>

              {/* STAGE 4: GEKLICKT */}
              <div className="funnel-stage">
                <div className="funnel-bar-fill" style={{ width: `${clickRatePercent}%`, background: '#06b6d4' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                  <div>
                    <strong style={{ color: '#22d3ee' }}>4. Link Geklickt &amp; Interagiert</strong>
                    <div className="text-xs text-muted">Eindeutige Klicks auf Links</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <strong style={{ fontSize: '1.2rem', color: '#22d3ee' }}>{uniqueClicks.toLocaleString('de-DE')}</strong>
                    <div className="text-xs text-muted">{percent(s.clickRate)} CTR ({ctor.toFixed(1)}% CTOR)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: LINK HEATMAP */}
      {activeTab === 'heatmap' && (
        <div className="card">
          <div className="card__body stack" style={{ gap: '1.25rem', padding: '1.5rem' }}>
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
                🎯 Link-Klick Heatmap &amp; Interaktions-Ranking
              </h3>
              <p className="text-muted text-sm" style={{ marginTop: '0.25rem' }}>
                Welche Links wurden von Empfängern am häufigsten aufgerufen?
              </p>
            </div>

            {linkStats && linkStats.length > 0 ? (
              <div className="stack" style={{ gap: '0.85rem' }}>
                {linkStats.map((link, idx) => {
                  const totalClicks = uniqueClicks || 1;
                  const percentage = Math.round((link.uniqueClicks / totalClicks) * 100);
                  return (
                    <div key={link.url} className="heat-item">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', overflow: 'hidden' }}>
                          <span
                            style={{
                              background: idx === 0 ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(255, 255, 255, 0.1)',
                              color: '#ffffff',
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 800,
                              fontSize: '0.75rem',
                              flexShrink: 0,
                            }}
                          >
                            #{idx + 1}
                          </span>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#60a5fa', fontWeight: 600, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {link.url}
                          </a>
                        </div>
                        <div style={{ flexShrink: 0, textAlign: 'right' }}>
                          <strong style={{ color: '#f8fafc' }}>{link.uniqueClicks}</strong>{' '}
                          <span className="text-xs text-muted">Klicks ({percentage}%)</span>
                        </div>
                      </div>

                      <div
                        style={{
                          width: '100%',
                          height: '8px',
                          background: 'rgba(255, 255, 255, 0.08)',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${percentage}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                            borderRadius: '4px',
                            transition: 'width 0.6s ease',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted text-sm" style={{ padding: '2rem 0', textAlign: 'center' }}>
                📌 Noch keine Link-Klicks registriert. Klicks erscheinen hier in Echtzeit.
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4: DELIVERABILITY & SKIP REASONS */}
      {activeTab === 'deliverability' && (
        <div className="stack" style={{ gap: '1.25rem' }}>
          <div className="card">
            <div className="card__body stack" style={{ padding: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
                🛡️ Zustellbarkeits-Analyse &amp; Ausschlussgründe
              </h3>
              
              {Object.keys(skipReasons).length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                  {Object.entries(skipReasons).map(([reason, count]) => (
                    <div
                      key={reason}
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        borderRadius: '10px',
                        padding: '1rem',
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#f87171' }}>
                        {count}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#fca5a5', fontWeight: 600, marginTop: '0.2rem' }}>
                        {reason === 'suppressed' ? 'Sperrliste / Gesperrt' : reason === 'unsubscribed' ? 'Abgemeldet' : reason === 'bounced' ? 'Unzustellbar (Bounce)' : reason}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#34d399', fontSize: '0.9rem', padding: '1rem 0' }}>
                  ✅ Saubere Zustellung: Keine Empfänger wurden aufgrund von Sperren oder Bounces ausgeschlossen.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
